import { useEffect, useRef } from "react";
import "./Field.css";

/**
 * The one and only background layer.
 *
 * Replaces the old Aurora blobs + particle canvas + CSS dot grid, which used to
 * stack five independently animated surfaces behind the content. A single
 * canvas keeps the compositing cost fixed, and — more to the point — gives the
 * page one background idea instead of several competing ones.
 *
 * The idea: the grid is a *material*, not a texture. It is nearly invisible at
 * rest, and everything it does is a reaction to something the reader did —
 *
 *   · the cursor pushes dots out of formation and lights the pool it stands in
 *   · a click sends a ring through the field that shoves each dot as it passes
 *   · scrolling fast smears the dots into streaks, which settle when you stop
 *
 * Nothing here plays to an empty room. If the reader is still, so is the page.
 */

/** px between dots */
const SPACING = 32;
/** dot size at rest */
const DOT = 1.3;
/** how far the cursor's influence reaches */
const RADIUS = 210;
/** quantised brightness steps — lets us batch every dot of one step into a single fill */
const LEVELS = 7;

/**
 * Alpha at rest vs. directly under the cursor. The gap between these two
 * numbers *is* the effect — keep it narrow enough that the field never
 * competes with the text sitting on top of it.
 */
const A_REST = 0.055;
const A_PEAK = 0.42;

/**
 * How far, in px, a dot is shoved out of its grid position at the very centre
 * of the cursor's influence. Small on purpose: the grid should look like it
 * *flexed*, not like it came apart. Past roughly 30 the rows stop reading as
 * rows and the whole thing turns to noise.
 */
const PUSH = 24;

/** Ripple: px/sec the wavefront travels, its thickness, and how long it lives. */
const RIPPLE_SPEED = 700;
const RIPPLE_BAND = 96;
const RIPPLE_LIFE = 1700;
/** How hard the wavefront shoves a dot as it passes through it. */
const RIPPLE_PUSH = 17;
/** More than a few overlapping rings is mush — oldest is dropped. */
const RIPPLE_MAX = 4;

/** Peak streak length, in px, added to a dot's height at full scroll speed. */
const SMEAR_MAX = 15;

interface Ripple {
    x: number;
    y: number;
    /** performance.now() at the moment of the click */
    born: number;
}

export default function Field() {
    const wrapRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const wrap = wrapRef.current;
        const canvas = canvasRef.current;
        if (!wrap || !canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const coarse = window.matchMedia("(hover: none)").matches;

        let cols = 0;
        let rows = 0;
        let w = 0;
        let h = 0;
        /** brightness step per dot, reallocated on resize */
        let levels = new Uint8Array(0);
        /** final on-screen position per dot, interleaved x,y — see draw() */
        let pos = new Float32Array(0);

        // Where the pointer is, and the lagging value we actually draw with. The
        // gap between them is what makes the light feel like it has weight.
        const target = { x: -9999, y: -9999 };
        const eased = { x: -9999, y: -9999 };

        /**
         * How much the cursor is currently affecting the field, 0–1.
         *
         * Separate from position on purpose. Parking `eased` off-screen on
         * pointer-leave — which is what this used to do — meant the light
         * didn't switch off, it *flew* off, dragging its pool of displaced
         * dots across the whole page on the way out. Fading a scalar leaves
         * the light exactly where the cursor left it and lets it die there.
         */
        let power = 0;
        let powerTarget = 0;

        const ripples: Ripple[] = [];

        /** Live scroll speed in px/frame, decayed toward 0 — drives the smear. */
        let scrollV = 0;
        let lastScrollY = window.scrollY;

        /**
         * Measures the wrapper rather than `window.innerWidth`.
         *
         * A ResizeObserver re-runs this whenever the element actually has a new
         * size, which matters because a canvas measured once at zero — a tab
         * that has not been composited yet, a pane opened later — would
         * otherwise stay zero forever, since no window `resize` event is ever
         * coming to correct it.
         */
        const resize = () => {
            // Capped low: the whole viewport is re-uploaded every frame, and
            // 1.3px dots don't gain anything visible past ~1.25x.
            const dpr = Math.min(window.devicePixelRatio || 1, 1.25);
            w = wrap.clientWidth;
            h = wrap.clientHeight;
            if (w === 0 || h === 0) return false;

            canvas.width = Math.floor(w * dpr);
            canvas.height = Math.floor(h * dpr);
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            cols = Math.ceil(w / SPACING) + 2;
            rows = Math.ceil(h / SPACING) + 2;
            levels = new Uint8Array(cols * rows);
            pos = new Float32Array(cols * rows * 2);
            return true;
        };

        const draw = (t: number) => {
            if (w === 0 || h === 0) return;
            ctx.clearRect(0, 0, w, h);

            // A slow lissajous drift keeps the grid from reading as a static
            // texture, without ever being fast enough to notice directly.
            const driftX = reduced ? 0 : Math.sin(t / 9400) * 7;
            const driftY = reduced ? 0 : Math.cos(t / 11800) * 7;

            const r2 = RADIUS * RADIUS;

            // Retire finished ripples before the hot loop reads the array, so
            // the per-dot cost is only ever what's actually still on screen.
            for (let i = ripples.length - 1; i >= 0; i--) {
                if (t - ripples[i].born > RIPPLE_LIFE) ripples.splice(i, 1);
            }
            const nRipples = ripples.length;

            // Precompute each live ripple's current radius and fade once per
            // frame rather than once per dot per ripple.
            const rr = new Float32Array(nRipples);
            const rFade = new Float32Array(nRipples);
            for (let i = 0; i < nRipples; i++) {
                const age = t - ripples[i].born;
                rr[i] = (age / 1000) * RIPPLE_SPEED;
                // Ease out, so the ring thins as it goes rather than vanishing
                // at full strength when its timer happens to run out.
                const p = 1 - age / RIPPLE_LIFE;
                rFade[i] = p * p;
            }

            // Pass 1 — for every dot, accumulate how much energy is acting on
            // it (cursor pool + any wavefront passing through) and where that
            // energy has pushed it. Position and brightness come out of the
            // same numbers, which is what makes the two read as one physical
            // event rather than two effects that happen to fire together.
            for (let y = 0; y < rows; y++) {
                for (let x = 0; x < cols; x++) {
                    const bx = x * SPACING + driftX;
                    const by = y * SPACING + driftY;

                    let energy = 0;
                    let ox = 0;
                    let oy = 0;

                    if (power > 0.001) {
                        const dx = bx - eased.x;
                        const dy = by - eased.y;
                        const d2 = dx * dx + dy * dy;
                        if (d2 < r2) {
                            // Squared falloff, then squared again: a tight pool
                            // of light rather than a wide wash.
                            const k = (1 - d2 / r2) * power;
                            const k2 = k * k;
                            energy += k2;
                            // Normalise the outward vector. The tiny epsilon is
                            // for the one frame where the cursor sits exactly on
                            // a dot — without it that dot's offset is NaN and it
                            // vanishes for as long as you hold still there.
                            const inv = 1 / (Math.sqrt(d2) + 0.0001);
                            const shove = k2 * PUSH;
                            ox += dx * inv * shove;
                            oy += dy * inv * shove;
                        }
                    }

                    for (let i = 0; i < nRipples; i++) {
                        const dx = bx - ripples[i].x;
                        const dy = by - ripples[i].y;
                        const d = Math.sqrt(dx * dx + dy * dy);
                        // Distance from this dot to the wavefront itself, not
                        // to its origin — the ring is a band, not a disc.
                        const band = 1 - Math.abs(d - rr[i]) / RIPPLE_BAND;
                        if (band <= 0) continue;
                        const amp = band * band * rFade[i];
                        energy += amp;
                        const inv = 1 / (d + 0.0001);
                        ox += dx * inv * amp * RIPPLE_PUSH;
                        oy += dy * inv * amp * RIPPLE_PUSH;
                    }

                    const idx = y * cols + x;
                    levels[idx] = Math.min(LEVELS - 1, (energy * LEVELS) | 0);
                    pos[idx * 2] = bx + ox;
                    pos[idx * 2 + 1] = by + oy;
                }
            }

            // Fast scrolling stretches each dot into a streak — the same read
            // as motion blur on a panned photograph, and the reason the field
            // feels attached to the page rather than painted behind it.
            const smear = Math.min(Math.abs(scrollV) * 0.32, SMEAR_MAX);

            // Pass 2 — one path (and one fill) per brightness step. Batching this
            // way is why a few thousand dots stay cheap enough for 60fps.
            for (let l = 0; l < LEVELS; l++) {
                const k = l / (LEVELS - 1);
                ctx.globalAlpha = A_REST + (A_PEAK - A_REST) * k;
                // Lit dots take on the light's colour; dots at rest stay the
                // neutral grey of the material they're made of.
                ctx.fillStyle = l === 0 ? "#8f97a3" : "#dce8ff";
                const size = DOT + k * 1.4;
                const halfW = size / 2;
                const halfH = (size + smear) / 2;
                ctx.beginPath();
                for (let y = 0; y < rows; y++) {
                    for (let x = 0; x < cols; x++) {
                        const idx = y * cols + x;
                        if (levels[idx] !== l) continue;
                        ctx.rect(
                            pos[idx * 2] - halfW,
                            pos[idx * 2 + 1] - halfH,
                            size,
                            size + smear
                        );
                    }
                }
                ctx.fill();
            }

            // A soft pool of light under the cursor, drawn over the dots.
            // Cheaper and softer than trying to get the same glow out of the
            // grid itself, and it gives the displaced dots something to be
            // displaced *within*.
            if (power > 0.01) {
                const R = RADIUS * 1.15;
                const g = ctx.createRadialGradient(
                    eased.x,
                    eased.y,
                    0,
                    eased.x,
                    eased.y,
                    R
                );
                g.addColorStop(0, `rgba(150, 185, 255, ${0.052 * power})`);
                g.addColorStop(0.45, `rgba(120, 160, 240, ${0.02 * power})`);
                g.addColorStop(1, "rgba(120, 160, 240, 0)");
                ctx.globalAlpha = 1;
                ctx.fillStyle = g;
                ctx.fillRect(eased.x - R, eased.y - R, R * 2, R * 2);
            }

            ctx.globalAlpha = 1;
        };

        // Reduced motion: paint the resting grid once and never start a loop.
        // Coarse pointers keep the loop, but only while something is actually
        // happening (see `settled` below) — there's no cursor to idle for, yet
        // a tap should still send a ring through the field.
        const isStatic = reduced;

        let raf = 0;
        let running = false;

        /** Nothing left to animate: no ripples, no scroll, no cursor. */
        const settled = () =>
            ripples.length === 0 &&
            Math.abs(scrollV) < 0.05 &&
            power < 0.002 &&
            powerTarget === 0;

        /** Counts frames while nothing but the slow drift is moving. */
        let idleTick = 0;

        const frame = (t: number) => {
            // At rest only the lissajous drift moves (~1px/s): half the frame
            // rate is indistinguishable and halves the full-screen repaint.
            if (!coarse && settled() && idleTick++ % 2 === 1) {
                raf = requestAnimationFrame(frame);
                return;
            }
            eased.x += (target.x - eased.x) * 0.11;
            eased.y += (target.y - eased.y) * 0.11;
            power += (powerTarget - power) * 0.09;
            scrollV *= 0.86;

            draw(t);

            // On a device with a cursor the loop is always warranted — the
            // lissajous drift alone is a reason to keep drawing. Without one,
            // stop as soon as the last ripple has died out.
            if (!coarse || !settled()) {
                raf = requestAnimationFrame(frame);
            } else {
                running = false;
                // One last frame at true rest, so the field doesn't freeze
                // mid-decay on whatever the final animated values happened
                // to be.
                power = 0;
                scrollV = 0;
                draw(t);
            }
        };

        const wake = () => {
            if (running || document.hidden) return;
            running = true;
            raf = requestAnimationFrame(frame);
        };

        const observer = new ResizeObserver(() => {
            if (!resize()) return;
            if (isStatic) draw(0);
            else wake();
        });
        observer.observe(wrap);

        if (isStatic) {
            return () => observer.disconnect();
        }

        const onPointerMove = (e: PointerEvent) => {
            target.x = e.clientX;
            target.y = e.clientY;
            // First sighting of the cursor: teleport rather than lerp, or the
            // pool visibly flies in from the top-left corner on page load.
            if (powerTarget === 0 && power < 0.002) {
                eased.x = e.clientX;
                eased.y = e.clientY;
            }
            powerTarget = 1;
            wake();
        };

        const onPointerLeave = () => {
            powerTarget = 0;
        };

        const onPointerDown = (e: PointerEvent) => {
            if (ripples.length >= RIPPLE_MAX) ripples.shift();
            ripples.push({ x: e.clientX, y: e.clientY, born: performance.now() });
            wake();
        };

        const onScroll = () => {
            const y = window.scrollY;
            scrollV = y - lastScrollY;
            lastScrollY = y;
            wake();
        };

        // Don't burn a rAF loop on a tab nobody is looking at.
        const onVisibility = () => {
            cancelAnimationFrame(raf);
            running = false;
            if (!document.hidden) wake();
        };

        window.addEventListener("pointermove", onPointerMove, { passive: true });
        window.addEventListener("pointerdown", onPointerDown, { passive: true });
        window.addEventListener("scroll", onScroll, { passive: true });
        document.addEventListener("pointerleave", onPointerLeave);
        document.addEventListener("visibilitychange", onVisibility);
        wake();

        return () => {
            cancelAnimationFrame(raf);
            observer.disconnect();
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerdown", onPointerDown);
            window.removeEventListener("scroll", onScroll);
            document.removeEventListener("pointerleave", onPointerLeave);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    return (
        <div className="field" ref={wrapRef} aria-hidden="true">
            {/*
                Three very large, very faint colour washes drifting on their own
                slow cycles. This is what stops the lifted background from
                reading as flat grey card stock: at these opacities you never
                see a colour, you only notice that the dark isn't uniform — the
                same way a room lit from two sides is never one shade of black.
                Pure CSS transforms, so they cost a composite and no script.
            */}
            <div className="field-aurora">
                <span className="aurora-blob aurora-a" />
                <span className="aurora-blob aurora-b" />
                <span className="aurora-blob aurora-c" />
            </div>
            <canvas ref={canvasRef} className="field-canvas" />
        </div>
    );
}
